import React, { useEffect, useRef } from 'react';
import { CoachPlacementSide, ARROW, CARD_W, COACH_Z } from '../../lib/coachPlacement';
import { renderInlineBold, splitParagraphs } from './inlineBold';
import { isTouchDevice, localizeVerbs } from '../../lib/coachVerbs';

/**
 * CoachMarkCard — the presentational tour card. Pure props in, callbacks
 * out: no framer-motion, no CoachMarkProvider/useCoachMark import. That's
 * what lets it mount in an RTL test with zero `jest.mock` boilerplate,
 * unlike almost every other animated component in this app.
 *
 * Animation is a CSS class (`.coach-mark-card`, index.css) rather than a
 * framer `initial`/`animate` pair, for the same reason: framer would need
 * to own `transform`, but CoachMarkHost drives `top`/`left` every frame
 * while the anchor moves, and owning both would fight.
 *
 * Two kinds share the shell. A `tour` card has Back / Next / "2 of 9". A
 * `nudge` is a single card that reacts to something the user just did
 * ("Your app is ready") — one "Got it" button, no counter, and it never
 * steals focus, because it tends to appear while the person is typing.
 */
export type CoachCardKind = 'tour' | 'nudge';

export interface CoachMarkCardProps {
  title: string;
  /** Plain text; blank line = paragraph; only `**bold**` inline. */
  body: string;
  stepNumber: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  /** Unanchored fallback — render fixed-centred with no arrow, because
   *  nothing on the page resolved for this step. */
  centered?: boolean;
  top?: number;
  left?: number;
  placement?: CoachPlacementSide;
  arrowOffset?: number;
  kind?: CoachCardKind;
  /** "New" chip — a card added since this person last finished the tour. */
  isNew?: boolean;
  /** One short line under the body ("Try it: click here"). */
  hint?: string;
  /** Focus the card on open (default). Nudges pass false. */
  autoFocus?: boolean;
  /** Hide "N of M" — optional cards that appear only when their target does
   *  are not part of the count. */
  showCounter?: boolean;
}

const FONT = 'DM Sans, sans-serif';

// The rendered arrow is a single 10×10 div rotated 45deg with only the two
// outward-facing borders set — no pseudo-elements, no SVG. Each placement
// sticks the diamond out of a different edge, pointing back at the anchor.
function arrowStyle(placement: CoachPlacementSide, offset: number): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    width: ARROW,
    height: ARROW,
    background: '#1a1a1a',
    transform: 'rotate(45deg)',
    pointerEvents: 'none',
  };
  const border = '1px solid rgba(255,255,255,0.10)';
  switch (placement) {
    case 'top': // card sits above the anchor — arrow pokes out of the bottom, pointing down
      return { ...base, bottom: -ARROW / 2 - 1, left: offset - ARROW / 2, borderRight: border, borderBottom: border };
    case 'bottom': // card sits below — arrow pokes out of the top, pointing up
      return { ...base, top: -ARROW / 2 - 1, left: offset - ARROW / 2, borderLeft: border, borderTop: border };
    case 'left': // card sits left of the anchor — arrow pokes out of the right, pointing right
      return { ...base, right: -ARROW / 2 - 1, top: offset - ARROW / 2, borderTop: border, borderRight: border };
    case 'right': // card sits right of the anchor — arrow pokes out of the left, pointing left
      return { ...base, left: -ARROW / 2 - 1, top: offset - ARROW / 2, borderBottom: border, borderLeft: border };
  }
}

export const CoachMarkCard = React.forwardRef<HTMLDivElement, CoachMarkCardProps>(function CoachMarkCard(
  {
    title, body, stepNumber, totalSteps, isFirstStep, isLastStep, onNext, onBack, onClose,
    centered, top, left, placement, arrowOffset,
    kind = 'tour', isNew = false, hint, autoFocus = true, showCounter = true,
  },
  ref,
) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const isNudge = kind === 'nudge';

  // Focus the card once, when a run opens — never on step change, which
  // would break Enter-to-advance (the primary interaction). The Host keys
  // this component per run (`key={kind:id}`), so an empty-deps effect is
  // exactly "once on open". Nudges opt out: they can appear while the person
  // is mid-sentence in the chat box, and yanking focus there is hostile; the
  // aria-live region below still announces them.
  useEffect(() => {
    if (autoFocus) innerRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRefs = (node: HTMLDivElement | null) => {
    innerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  // Enter/arrow keys only act when the card itself has focus — if focus has
  // moved to the Back/Next button, the button's own click handles Enter,
  // and re-firing here would double-advance.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === 'ArrowRight') {
      e.preventDefault();
      onNext();
    } else if (e.key === 'ArrowLeft' && !isFirstStep && !isNudge) {
      e.preventDefault();
      onBack();
    }
  };

  const positionStyle: React.CSSProperties = centered
    ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    : { position: 'fixed', top, left };

  // `{click}` in the copy becomes "click" or "tap" for this device.
  const touch = isTouchDevice();
  const shownTitle = localizeVerbs(title, touch);
  const shownHint = hint ? localizeVerbs(hint, touch) : hint;
  const paragraphs = splitParagraphs(localizeVerbs(body, touch));

  return (
    <div
      ref={setRefs}
      role="dialog"
      aria-modal="false"
      aria-labelledby="coach-mark-title"
      aria-describedby="coach-mark-body"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="coach-mark-card"
      data-coach-kind={kind}
      style={{
        ...positionStyle,
        zIndex: COACH_Z,
        width: CARD_W,
        maxWidth: 'calc(100vw - 16px)',
        background: '#1a1a1a',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.25)',
        padding: 16,
        fontFamily: FONT,
        outline: 'none',
      }}
    >
      {!centered && placement && arrowOffset !== undefined && (
        <div aria-hidden="true" style={arrowStyle(placement, arrowOffset)} />
      )}

      <button
        type="button"
        onClick={onClose}
        aria-label={isNudge ? 'Dismiss' : 'End tour'}
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: 8,
          color: '#8a8a8a',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#f5f5f5'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#8a8a8a'; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      <div aria-live="polite">
        <h3 id="coach-mark-title" style={{ margin: '0 24px 6px 0', fontSize: 14, fontWeight: 600, color: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew && (
            <span
              className="coach-mark-new"
              aria-label="New in this update"
              style={{
                display: 'inline-block',
                padding: '1px 6px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: '#9db4ff',
                background: 'rgba(50, 100, 248, 0.15)',
                flexShrink: 0,
              }}
            >
              New
            </span>
          )}
          <span>{shownTitle}</span>
        </h3>
        <div id="coach-mark-body" style={{ fontSize: 13, lineHeight: 1.5, color: '#a3a3a3' }}>
          {paragraphs.map((p, i) => (
            <p key={i} style={{ margin: i === 0 ? 0 : '8px 0 0' }}>
              {renderInlineBold(p, `p${i}`)}
            </p>
          ))}
        </div>
        {shownHint && (
          <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.4, color: '#7fa0ff' }}>{shownHint}</p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: isNudge || !showCounter ? 'flex-end' : 'space-between', marginTop: 16 }}>
        {!isNudge && showCounter && (
          <span aria-label={`Step ${stepNumber} of ${totalSteps}`} style={{ fontSize: 12, color: '#8a8a8a' }}>
            {stepNumber} of {totalSteps}
          </span>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {!isNudge && !isFirstStep && (
            <button
              type="button"
              onClick={onBack}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'transparent',
                color: '#d4d4d8',
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: 'none',
              background: '#3264F8',
              color: '#fff',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {isNudge ? 'Got it' : isLastStep ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
});
